import {
  Alert,
  Button,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCheck,
  IconFolder,
  IconFolderOff,
} from "@tabler/icons-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { client } from "@/api-client";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import { TrainerRoutes, useNavigator } from "@/routers/routes";

export const MoveToFolder: React.FC = () => {
  const { itemId, itemType } = useParams<{
    itemId: string;
    itemType: string;
  }>();
  const { navigate } = useNavigator();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [isMoving, setIsMoving] = useState<boolean>(false);
  const [itemName, setItemName] = useState<string>("");
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);

  // Fetch file/folder list to get current structure
  const listFiles = client.useQuery(
    "get",
    "/universal/files/",
    {},
    {
      staleTime: 0,
      gcTime: 0,
    },
  );

  // Move mutations
  const moveFile = client.useMutation("post", "/universal/files/{fileId}/move");
  const moveFolder = client.useMutation(
    "post",
    "/universal/folders/{folderId}/move",
  );

  // Get the item details on load
  useEffect(() => {
    if (listFiles.data && itemId) {
      if (itemType === "file") {
        const file = listFiles.data.files.find((f) => f.id === itemId);
        if (file) {
          setItemName(file.name);
          // Ensure parentFolderId is not undefined
          const parentId =
            file.parentFolderId === undefined ? null : file.parentFolderId;
          setCurrentFolderId(parentId);
        }
      } else if (itemType === "folder") {
        const folder = listFiles.data.folders.find((f) => f.id === itemId);
        if (folder) {
          setItemName(folder.name);
          // Ensure parentFolderId is not undefined
          const parentId =
            folder.parentFolderId === undefined ? null : folder.parentFolderId;
          setCurrentFolderId(parentId);
        }
      }
    }
  }, [listFiles.data, itemId, itemType]);

  if (listFiles.isLoading) {
    return <FancyLoader />;
  }

  if (!listFiles.data) {
    return (
      <Stack>
        <Header title="Move to Folder" />
        <Alert color="red" title="Error" icon={<IconAlertCircle />}>
          Failed to load folder structure. Please try again.
        </Alert>
        <Button onClick={() => navigate({ path: TrainerRoutes.LIBRARY.LIST })}>
          Return to Library
        </Button>
      </Stack>
    );
  }

  // Get the folder structure
  const allFolders = listFiles.data.folders;
  const rootFolders = allFolders.filter(
    (folder) => folder.parentFolderId === null,
  );

  const getFolderChildren = (parentId: string) => {
    return allFolders.filter((folder) => {
      // Exclude the folder we're moving (prevents circular references)
      if (itemType === "folder" && folder.id === itemId) {
        return false;
      }
      return folder.parentFolderId === parentId;
    });
  };

  // Get path for a folder (breadcrumb style display)
  const getFolderPath = (folderId: string | null): string => {
    if (folderId === null) return "Root";

    const folder = allFolders.find((f) => f.id === folderId);
    if (!folder) return "Unknown";

    if (folder.parentFolderId === null) {
      return `Root > ${folder.name}`;
    }

    // Ensure parentFolderId is not undefined
    const parentId =
      folder.parentFolderId === undefined ? null : folder.parentFolderId;
    return `${getFolderPath(parentId)} > ${folder.name}`;
  };

  // Handle the move operation
  const handleMove = async () => {
    // Don't move to the same folder
    if (selectedFolderId === currentFolderId) {
      setError("This item is already in this folder");
      return;
    }

    setIsMoving(true);
    setError(null);

    try {
      if (itemType === "file") {
        await moveFile.mutateAsync({
          params: {
            path: { fileId: itemId },
          },
          body: {
            parentFolderId: selectedFolderId,
          },
        });
      } else if (itemType === "folder") {
        await moveFolder.mutateAsync({
          params: {
            path: { folderId: itemId },
          },
          body: {
            parentFolderId: selectedFolderId,
          },
        });
      }

      setSuccess(true);

      // Return to library after a short delay to show success message
      setTimeout(() => {
        navigate({ path: TrainerRoutes.LIBRARY.LIST });
      }, 1500);
    } catch (error) {
      console.error("Error moving item:", error);
      setError("Failed to move item. Please try again.");
      setIsMoving(false);
    }
  };

  // Render a folder and its children recursively
  const renderFolder = (folder: (typeof allFolders)[0], depth = 0) => {
    const children = getFolderChildren(folder.id);
    const isCurrentFolder = folder.id === currentFolderId;
    const isSelected = folder.id === selectedFolderId;

    // If we're moving a folder, don't show it or its descendants as options
    if (itemType === "folder" && folder.id === itemId) {
      return null;
    }

    return (
      <div key={folder.id} style={{ marginLeft: depth * 20 }}>
        <Paper
          p="sm"
          withBorder
          style={{
            cursor: isCurrentFolder ? "not-allowed" : "pointer",
            opacity: isCurrentFolder ? 0.5 : 1,
            marginBottom: 8,
            background: isSelected ? "#f0f9ff" : undefined,
            borderColor: isSelected ? "#339af0" : undefined,
            borderWidth: isSelected ? 2 : 1,
          }}
          onClick={() => {
            if (!isCurrentFolder) {
              setSelectedFolderId(folder.id);
              setError(null);
            }
          }}
        >
          <Group>
            <IconFolder
              size="1.2rem"
              color={isSelected ? "#339af0" : undefined}
            />
            <Text fw={isSelected ? 600 : 400}>{folder.name}</Text>
            {isCurrentFolder && (
              <Text size="xs" c="dimmed">
                (current location)
              </Text>
            )}
            {isSelected && (
              <IconCheck
                size="1rem"
                color="#339af0"
                style={{ marginLeft: "auto" }}
              />
            )}
          </Group>
        </Paper>
        {children.length > 0 && (
          <div>{children.map((child) => renderFolder(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <Stack>
      <Header title="Move to Folder" />

      <Title order={2} size="h3">{`Move "${itemName}"`}</Title>

      {success ? (
        <Alert color="green" title="Success!" icon={<IconCheck />}>
          Item successfully moved to{" "}
          {selectedFolderId === null ? "Root" : getFolderPath(selectedFolderId)}
        </Alert>
      ) : (
        <>
          <Text size="md" mb="md">
            Select a destination folder:
          </Text>

          {/* Root folder option */}
          <Paper
            p="md"
            withBorder
            style={{
              cursor: currentFolderId === null ? "not-allowed" : "pointer",
              opacity: currentFolderId === null ? 0.5 : 1,
              marginBottom: 8,
              background: selectedFolderId === null ? "#f0f9ff" : undefined,
              borderColor: selectedFolderId === null ? "#339af0" : undefined,
              borderWidth: selectedFolderId === null ? 2 : 1,
            }}
            onClick={() => {
              if (currentFolderId !== null) {
                setSelectedFolderId(null);
                setError(null);
              }
            }}
          >
            <Group>
              <IconFolderOff
                size="1.2rem"
                color={selectedFolderId === null ? "#339af0" : undefined}
              />
              <Text fw={selectedFolderId === null ? 600 : 400}>Root</Text>
              {currentFolderId === null && (
                <Text size="xs" c="dimmed">
                  (current location)
                </Text>
              )}
              {selectedFolderId === null && (
                <IconCheck
                  size="1rem"
                  color="#339af0"
                  style={{ marginLeft: "auto" }}
                />
              )}
            </Group>
          </Paper>

          {/* Folder hierarchy */}
          <ScrollArea h={400} mt="md">
            <Stack>
              {rootFolders
                .filter(
                  (folder) => !(itemType === "folder" && folder.id === itemId),
                )
                .map((folder) => renderFolder(folder))}

              {rootFolders.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="md">
                  No folders available
                </Text>
              )}
            </Stack>
          </ScrollArea>

          {selectedFolderId !== null && (
            <Text size="sm" mt="xs">
              Selected destination: <b>{getFolderPath(selectedFolderId)}</b>
            </Text>
          )}

          {error && (
            <Alert color="red" title="Error" mt="xs">
              {error}
            </Alert>
          )}

          <Group justify="space-between" mt="xl">
            <Button
              variant="light"
              onClick={() => navigate({ path: TrainerRoutes.LIBRARY.LIST })}
              disabled={isMoving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMove}
              loading={isMoving}
              disabled={!selectedFolderId && currentFolderId !== null}
            >
              Move Item
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );
};
